import { type FC, memo } from "react";
import { shallowEqual, useSelector } from "react-redux";
import { Modal, TriggerButton, TriggerButtonGroup, TriggerButtonSize } from "widgets";
import { useOverlay } from "widgets/hooks";

import { conditionallyPluralizeTrackLabel } from "../../../app-utils/string.js";
import { type IPileup } from "../../../app-utils/track-shapes/track-pileup-factory.js";
import { type ProjectFile } from "../../../graphql.js";
import { type ISortableTrack } from "../../../redux/modules/genome-browser.js";
import { type IAppState } from "../../../redux/reducer.js";
import BedTrackCustomizationDialog from "../bed-track/BedsTrackCustomizationDialog.js";
import { FeaturesLimitToggleButton } from "../features-track/FeatureTrackHeader.js";
import { TrackHeader } from "../TrackHeader.js";

export interface IBedTrackHeaderProps {
  track: ISortableTrack;
  pileup: IPileup;
  overflow: boolean;
  showAllFeatures: boolean;
  bedFileId: number;

  isTrackHovered?: boolean;
}

const BedTrackHeader: FC<IBedTrackHeaderProps> = (props) => {
  const { pileup, showAllFeatures, overflow, track, bedFileId } = props;

  const bedFile = useSelector<IAppState, ProjectFile>(
    (state) => state.genomeBrowser.bedDatafiles[bedFileId],
    shallowEqual,
  );

  return (
    <TrackHeader
      title={conditionallyPluralizeTrackLabel(track)}
      subtitle={bedFile?.fileName}
      isTrackHovered={props.isTrackHovered}
      trackIcons={
        <TriggerButtonGroup vertical>
          <BedTrackOpener trackLabel={conditionallyPluralizeTrackLabel(track)} />
          <FeaturesLimitToggleButton
            track={track}
            trackSpec={{ ...track, fileId: bedFileId }}
            pileup={pileup}
            overflow={overflow}
            showAllFeatures={showAllFeatures}
          />
        </TriggerButtonGroup>
      }
    />
  );
};

const BedTrackOpener: FC<{ trackLabel: string }> = ({ trackLabel }) => {
  const { triggerProps, overlayProps, state } = useOverlay({});
  return (
    <>
      <TriggerButton
        icon={"settings"}
        tooltip={`Customize ${trackLabel} track`}
        label={"Configure"}
        size={TriggerButtonSize.Small}
        {...triggerProps}
      />
      <Modal {...overlayProps} state={state}>
        <BedTrackCustomizationDialog onClose={state.close} />
      </Modal>
    </>
  );
};

/// --- Export --- ///
const MemoBedTrackHeader = memo(BedTrackHeader, shallowEqual);

export { MemoBedTrackHeader as BedTrackHeader };
